import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lightbulb, X } from "lucide-react";

import FeatureRequestDialog from "./FeatureRequestDialog";
import { isFeatureEnabled } from "../../services/flagsService";
import { cn } from "@/lib/utils";

const OPEN_EVENT = "feature-request:open";
const NUDGE_SEEN_KEY = "feature_request_nudge_seen";
const NUDGE_DELAY_MS = 25_000;

// Package pages render their own back-to-top button in the bottom-right corner.
const PACKAGE_PAGE = /^\/packages\/(?!random$)[^/]+$/;

// Full-screen tools where a floating button would get in the way.
const HIDDEN_PREFIXES = ["/timer", "/buzzer", "/games/", "/sheets"];

/** Opens the feature request dialog from anywhere (e.g. the footer link). */
export const openFeatureRequest = () => window.dispatchEvent(new Event(OPEN_EVENT));

const readNudgeSeen = () => {
  try {
    return localStorage.getItem(NUDGE_SEEN_KEY) === "1";
  } catch {
    return true; // no storage: never nag
  }
};

const markNudgeSeen = () => {
  try {
    localStorage.setItem(NUDGE_SEEN_KEY, "1");
  } catch {
    // ignore
  }
};

/**
 * Owns the single feature request dialog and renders a floating "idea" button
 * (bottom-right; raised on package pages to clear their back-to-top button).
 * First-time visitors get a one-off speech-bubble nudge after a short delay.
 */
const FeatureRequestLauncher: React.FC = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const enabled = isFeatureEnabled("FEATURE_REQUEST");
  const raised = PACKAGE_PAGE.test(pathname);
  const hidden = HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : p + "/"));

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!enabled || hidden || readNudgeSeen()) return;
    const id = window.setTimeout(() => setNudge(true), NUDGE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [enabled, hidden]);

  const dismissNudge = () => {
    setNudge(false);
    markNudgeSeen();
  };

  const launch = () => {
    dismissNudge();
    setOpen(true);
  };

  if (!enabled) return null;

  return (
    <>
      {/* right-6 + 44px button puts its center 46px from the edge, the same column as
          the package page's 40px back-to-top button at right-[26px] */}
      {!hidden && (
        <div className={cn("fixed right-6 z-40 flex flex-row-reverse items-end gap-2 print:hidden", raised ? "bottom-[4.75rem]" : "bottom-4")}>
          <button
            type="button"
            onClick={launch}
            aria-label="Funksiya təklif et"
            className={cn(
              "group appearance-none !rounded-full h-11 px-3 hover:pr-4 focus-visible:pr-4",
              "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-orange-500/30",
              "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950"
            )}
          >
            {/* Inner flex span: Reboot.css's -webkit-appearance: button breaks flex on the button itself */}
            <span className="flex items-center gap-0 group-hover:gap-2 group-focus-visible:gap-2 transition-all duration-300">
            <span className="relative flex">
              <Lightbulb className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-12" />
              {nudge && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-white animate-ping" />}
            </span>
            <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold transition-all duration-300 group-hover:max-w-[8rem] group-focus-visible:max-w-[8rem]">
              Təklif et
            </span>
            </span>
          </button>

          {nudge && (
            <div className="relative mb-1.5 max-w-[15rem] rounded-2xl rounded-br-sm border border-amber-100 dark:border-amber-900 bg-white dark:bg-gray-900 px-3.5 py-2.5 shadow-lg animate-in fade-in slide-in-from-right-2 duration-300">
              <button
                type="button"
                onClick={dismissNudge}
                aria-label="Bağla"
                className="appearance-none absolute top-1.5 right-1.5 p-0.5 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <p className="pr-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Fikriniz var? 💡</p>
              <button
                type="button"
                onClick={launch}
                className="appearance-none text-left text-xs leading-snug text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-300"
              >
                Saytda nəyi görmək istədiyinizi bizə yazın.
              </button>
            </div>
          )}
        </div>
      )}

      <FeatureRequestDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

export default FeatureRequestLauncher;
